
/**
 * Normalize a room `users` entry (populated object or bare id) to an id string.
 * @param {unknown} user
 * @returns {string|null}
 */
export function roomUserId(user) {
  if (user == null) return null;
  if (typeof user === "string" || typeof user === "number") {
    const s = String(user).trim();
    return s || null;
  }
  if (typeof user === "object") {
    const raw = user._id ?? user.id;
    if (raw == null || raw === "") return null;
    return String(raw);
  }
  return null;
}

/**
 * Ids from a room users array (objects and/or bare ids).
 * @param {unknown[]} users
 * @returns {string[]}
 */
export function roomUserIds(users) {
  if (!Array.isArray(users)) return [];
  return users.map(roomUserId).filter(Boolean);
}

/**
 * Pick the visitor (non-agent) id from a room users list.
 * Unassigned rooms often return bare user ids; assigned rooms return objects.
 * @param {unknown[]} users
 * @param {string|null|undefined} loggedInAgentId
 * @returns {string|null}
 */
export function resolveVisitorIdFromRoomUsers(users, loggedInAgentId) {
  const agent = loggedInAgentId != null ? String(loggedInAgentId) : "";
  const list = Array.isArray(users) ? users : [];

  const guest = list.find((u) => {
    const id = roomUserId(u);
    return id && id !== agent && typeof u === "object" && u?.is_guest;
  });
  if (guest) return roomUserId(guest);

  const nonAgent = list.find((u) => {
    const id = roomUserId(u);
    return id && id !== agent;
  });
  if (nonAgent) return roomUserId(nonAgent);

  // Last resort: sole occupant (typical unassigned visitor-only room).
  if (list.length === 1) return roomUserId(list[0]);
  return null;
}

/**
 * People/CRM customer id for ban + history APIs when get-customer embeds `crm`.
 * Falls back to the chat user id (works with from_live_chat bans).
 * @param {object|null|undefined} user — payload.user from get-customer
 * @returns {{ id: string|null, userId: string|null, crmId: string|null }}
 */
export function resolvePeopleCustomerIdsFromGetCustomerUser(user, payload) {
  if (!user || typeof user !== "object") {
    // Top-level customer on get-customer response (no nested user.crm).
    if (payload && typeof payload === "object") {
      const top =
        roomUserId(payload.customer) ||
        roomUserId(payload.crm) ||
        roomUserId(payload.people_customer) ||
        (payload.customer_id != null && payload.customer_id !== ""
          ? String(payload.customer_id)
          : null);
      return { id: top, userId: null, crmId: top };
    }
    return { id: null, userId: null, crmId: null };
  }
  const userId = roomUserId(user);
  const crm = user.crm && typeof user.crm === "object" ? user.crm : null;
  const nestedCustomer =
    user.customer && typeof user.customer === "object" ? user.customer : null;
  const nestedPeople =
    user.people && typeof user.people === "object" ? user.people : null;

  const crmId =
    (crm ? roomUserId(crm) : null) ||
    (nestedCustomer ? roomUserId(nestedCustomer) : null) ||
    (nestedPeople ? roomUserId(nestedPeople) : null) ||
    (user.customer_id != null && user.customer_id !== ""
      ? String(user.customer_id)
      : null) ||
    (user.crm_id != null && user.crm_id !== ""
      ? String(user.crm_id)
      : null) ||
    (user.people_id != null && user.people_id !== ""
      ? String(user.people_id)
      : null) ||
    (payload && typeof payload === "object"
      ? roomUserId(payload.customer) ||
        roomUserId(payload.crm) ||
        (payload.customer_id != null && payload.customer_id !== ""
          ? String(payload.customer_id)
          : null)
      : null);

  // Do NOT fall back to chat user id — `/customer/ban/:id` is People CRM only.
  return { id: crmId, userId, crmId };
}

/**
 * Resolve visitor/customer id for live-chat actions (ban, details, etc.).
 * Prefer loaded sidebar customer (CRM id when present), then room customerId,
 * then non-agent roomUsers — never the logged-in agent.
 */
export function resolveLiveChatBanCustomerId({
  selectUser,
  customerData,
  loggedInAgentId,
} = {}) {
  const agent = loggedInAgentId != null ? String(loggedInAgentId) : "";
  const notAgent = (id) => {
    if (id == null || id === "") return null;
    const s = String(id);
    if (agent && s === agent) return null;
    return s;
  };

  const fromSidebar =
    notAgent(customerData?.[0]?.crmId) ||
    notAgent(customerData?.[0]?.id) ||
    null;
  if (fromSidebar) return fromSidebar;

  const fromSelect = notAgent(selectUser?.customerId);
  if (fromSelect) return fromSelect;

  return resolveVisitorIdFromRoomUsers(
    selectUser?.roomUsers,
    loggedInAgentId
  );
}

export function resolveCustomerIdFromGetMessagePayload(payload, options = {}) {
  if (!payload || typeof payload !== "object") return null;

  const raw = payload.customerId ?? payload.customer_id;
  if (raw != null && raw !== "") {
    if (typeof raw === "string") return raw;
    if (typeof raw === "object" && raw._id) return raw._id;
    return String(raw);
  }

  const crmId =
    roomUserId(payload.crm) ||
    roomUserId(payload.customer) ||
    roomUserId(payload.people);
  if (crmId) return crmId;

  const {
    prevCustomerId,
    loggedInAgentId,
    payloadUsers = [],
    visitorMessage = null,
  } = options;

  const agent = loggedInAgentId != null ? String(loggedInAgentId) : "";
  if (
    prevCustomerId != null &&
    prevCustomerId !== "" &&
    String(prevCustomerId) !== agent
  ) {
    return prevCustomerId;
  }

  const fromVisitorUser = resolveVisitorIdFromRoomUsers(
    payloadUsers,
    loggedInAgentId
  );

  const fromMessageRaw =
    visitorMessage?.sender ||
    visitorMessage?.senderId ||
    visitorMessage?.sender_id ||
    visitorMessage?.customerId ||
    visitorMessage?.userId ||
    visitorMessage?.sender?._id ||
    null;
  const fromMessage =
    fromMessageRaw != null && String(fromMessageRaw) !== agent
      ? String(
          typeof fromMessageRaw === "object"
            ? fromMessageRaw._id || fromMessageRaw.id
            : fromMessageRaw
        )
      : null;

  return (
    fromVisitorUser ||
    fromMessage ||
    roomUserId(payload.user) ||
    roomUserId(payload.visitor) ||
    null
  );
}
